import React, { memo, useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles, Text, Button, Icon } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';

import ConditionsContext from './ConditionsContext';
import { getEmptyCondition, getGroupConditionString } from './utils';
import Condition from './Condition';
import OperatorSwitch from './OperatorSwitch';

import styles from './styles/group';
const useStyles = createUseStyles(styles, 'ConditionsGroup');

const Group = ({
    expanded,
    group,
    index,
    onChange,
    onDelete,
    toggleExpanded,
    listMapFields,
    options,
}) => {
    const { t, locale } = useWeb5Context();
    const classes = useStyles();
    const { isReadOnly } = useContext(ConditionsContext);
    const { conditions, operator } = group;

    const onChangeGroup = useCallback(
        (op, cond) => {
            onChange(index, {
                operator: op,
                conditions: cond,
            });
        },
        [index, onChange],
    );

    const conditionString = useMemo(
        () => getGroupConditionString(group, locale, listMapFields),
        [group, locale, listMapFields],
    );

    const handleConditionChange = useCallback(
        (condIndex, values) => {
            const newConditions = [...conditions];
            newConditions[condIndex] = {
                ...newConditions[condIndex],
                ...values,
            };
            onChangeGroup(operator, newConditions);
        },
        [conditions, onChangeGroup, operator],
    );

    const handleAddCondition = useCallback(() => {
        const newConditions = [...conditions];
        newConditions.push(getEmptyCondition());
        onChangeGroup(operator, newConditions);
    }, [conditions, onChangeGroup, operator]);

    const handleRemoveCondition = useCallback(
        (removeIndex) => {
            const newConditions = [
                ...conditions.slice(0, removeIndex),
                ...conditions.slice(removeIndex + 1),
            ];
            if (newConditions.length === 0) {
                newConditions.push(getEmptyCondition());
            }
            onChangeGroup(operator, newConditions);
        },
        [conditions, onChangeGroup, operator],
    );

    const handleRemoveGroup = useCallback(() => {
        onDelete && onDelete(index);
    }, [index, onDelete]);

    const handleLogicOperatorChange = useCallback(
        (val) => {
            onChangeGroup(val, conditions);
        },
        [conditions, onChangeGroup],
    );

    const handleGroupClick = useCallback(() => {
        if (!expanded) {
            toggleExpanded(index);
        }
    }, [expanded, index, toggleExpanded]);

    const groupClassName = classnames(classes.root, {
        [classes.expanded]: expanded,
        [classes.collapsed]: !expanded,
        [classes.addConditionRight]: conditions?.length <= 1,
    });

    return (
        <section className={groupClassName} onClick={handleGroupClick}>
            <div className={classes.container}>
                <header className={classes.header}>
                    {conditions?.length > 1 && (
                        <OperatorSwitch
                            value={operator}
                            onChange={!isReadOnly ? handleLogicOperatorChange : undefined}
                        />
                    )}
                    {!isReadOnly && (
                        <Button
                            onClick={handleAddCondition}
                            type="terciary"
                            className="conditions__conditions-group__add-condition-button"
                        >
                            {t('cfm_action_add_condition')}
                        </Button>
                    )}
                </header>
                <main className={classes.body}>
                    {conditions.map((cond, i) => (
                        <Condition
                            key={`condition-${index}-${i}-${cond?.field?.value}`}
                            condition={cond}
                            index={i}
                            onChange={handleConditionChange}
                            onRemove={handleRemoveCondition}
                            options={options}
                        />
                    ))}
                </main>
            </div>
            <footer className={classes.footer}>
                <div>
                    {!expanded && <Icon name="chevron" size="small" />}
                    <Text className={classes.conditionString}>{conditionString}</Text>
                </div>
                {!isReadOnly && onDelete && (
                    <Button onClick={handleRemoveGroup} type="secondary-error">
                        {t('cfm_action_delete')}
                    </Button>
                )}
            </footer>
        </section>
    );
};

Group.propTypes = {
    expanded: PropTypes.bool.isRequired,
    group: PropTypes.shape({
        conditions: PropTypes.array,
        operation: PropTypes.string,
    }).isRequired,
    index: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    toggleExpanded: PropTypes.func.isRequired,
};

export default memo(Group);
